import { useEffect, useRef } from "react";
import { drawPetalShape, PETALS_COIN, type PetalShapeConfig } from "./petalsGraphic";

/**
 * The PETALS currency coin — a small procedural flower rendered to a canvas (ported from the Shopify app's
 * shape renderer). Animates its gradient/breathing only while on-screen (IntersectionObserver) so many of
 * them on a page stay cheap. Static config → still image; animated fills → a gentle looping shimmer.
 */
export function PetalsCoin({ size = 20, config = PETALS_COIN, animate = true, className }: {
  size?: number;
  config?: PetalShapeConfig;
  animate?: boolean;
  className?: string;
}) {
  const ref = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    canvas.width = Math.round(size * dpr);
    canvas.height = Math.round(size * dpr);
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.scale(dpr, dpr);

    const animated = animate && config.fillStyle === "animatedGradient";
    let raf = 0, running = false, start = performance.now();
    const frame = () => { drawPetalShape(ctx, size, config, performance.now() - start); if (running) raf = requestAnimationFrame(frame); };
    const startLoop = () => { if (!running && animated) { running = true; raf = requestAnimationFrame(frame); } };
    const stopLoop = () => { running = false; cancelAnimationFrame(raf); };

    // first paint (also the only paint for static configs)
    drawPetalShape(ctx, size, config, 0);

    let io: IntersectionObserver | null = null;
    if (animated) {
      io = new IntersectionObserver((es) => { es.some((e) => e.isIntersecting) && !document.hidden ? startLoop() : stopLoop(); }, { threshold: 0.1 });
      io.observe(canvas);
      const onVis = () => (document.hidden ? stopLoop() : startLoop());
      document.addEventListener("visibilitychange", onVis);
      return () => { stopLoop(); io?.disconnect(); document.removeEventListener("visibilitychange", onVis); };
    }
    return () => stopLoop();
  }, [size, config, animate]);

  return <canvas ref={ref} className={className} style={{ width: size, height: size, display: "inline-block", verticalAlign: "middle" }} aria-hidden="true" />;
}

/** A Petals price chip: the coin + the amount (e.g. "🌸 2,062"). */
export function PetalsPrice({ petals, size = 18, className }: { petals: number; size?: number; className?: string }) {
  return (
    <span className={"petals-price" + (className ? " " + className : "")}>
      <PetalsCoin size={size} />
      <span className="petals-amount">{petals.toLocaleString()}</span>
    </span>
  );
}
